// Pure-data Refine resource definitions — the single source of truth for
// resource names, route paths, menu labels, and grouping. No JSX here on
// purpose: node-side tooling (the e2e-visual crawler) imports this module to
// enumerate routes, so it must stay free of React/antd imports. App.tsx maps
// `meta.icon` (a name string) to the actual icon element at render time.

export interface ResourceDefMeta {
  label?: string;
  /** Key into the icon map in App.tsx (antd icon component name). */
  icon?: string;
  parent?: string;
  hide?: boolean;
  tag?: string;
}

export interface ResourceDef {
  name: string;
  list?: string;
  create?: string;
  edit?: string;
  show?: string;
  clone?: string;
  meta?: ResourceDefMeta;
}

export const resourceDefs: ResourceDef[] = [
  {
    name: "dashboard",
    list: "/",
    meta: { label: "Strona główna", icon: "HomeOutlined" },
  },
  {
    name: "admin-tasks",
    list: "/admin-tasks",
    meta: { label: "Zadania admin", icon: "CheckSquareOutlined", tag: "new" },
  },
  {
    name: "logistics-pipeline",
    list: "/logistics-pipeline",
    meta: { label: "Pipeline logistyki", icon: "UserSwitchOutlined" },
  },
  {
    name: "logistics-tasks",
    list: "/logistics-tasks",
    meta: { label: "Zadania logistyki", icon: "UserSwitchOutlined", tag: "new" },
  },
  {
    name: "logistics-load-alerts",
    list: "/logistics-load-alerts",
    meta: { label: "Alerty Logistyki", icon: "WarningOutlined" },
  },
  {
    name: "sales-tasks",
    list: "/sales-tasks",
    meta: { label: "Pulpit handlowca", icon: "UserSwitchOutlined", tag: "new" },
  },
  {
    name: "transactions",
    list: "/transactions",
    create: "/transactions/create",
    clone: "/transactions/:id/clone",
    edit: "/transactions/edit/:id",
    meta: { label: "Transakcje", icon: "SwapOutlined" },
  },
  {
    name: "transaction_transports",
    list: "/transactions/:transaction_id/transports",
    create: "/transactions/:transaction_id/transports/create",
    edit: "/transactions/:transaction_id/transports/edit/:id",
    meta: {
      label: "Transporty",
      icon: "CarOutlined",
      hide: true, // Hide from menu since it's nested
    },
  },
  {
    name: "products",
    list: "/products",
    meta: { label: "Produkty", icon: "AppstoreOutlined" },
  },
  {
    name: "companies",
    list: "/companies",
    meta: { label: "Firmy", icon: "ShopOutlined" },
  },
  {
    name: "contacts",
    create: "/companies/contacts/:companyId/create",
    edit: "/companies/contacts/:companyId/edit/:id",
    meta: {
      label: "Kontakty",
      icon: "ContactsOutlined",
      hide: true, // Hide from menu since it's nested
    },
  },
  {
    name: "price_lists",
    list: "/price-lists",
    meta: { label: "Cenniki", icon: "DollarOutlined" },
  },
  {
    name: "invoices",
    meta: { hide: true },
  },
  {
    name: "invoices-parent",
    meta: { label: "Faktury", icon: "FileTextOutlined" },
  },
  {
    name: "invoices-issued",
    list: "/invoices/issued",
    meta: { label: "Przychodowe", icon: "ArrowUpOutlined", parent: "invoices-parent" },
  },
  {
    name: "invoices-received",
    list: "/invoices/received",
    meta: { label: "Kosztowe", icon: "ArrowDownOutlined", parent: "invoices-parent" },
  },
  // Sync page removed — sync is now integrated into the received invoices list
  {
    // Document pipeline (queue + scan upload) — standalone,
    // ungated page available to every logged-in user.
    name: "pipeline_jobs",
    list: "/dokumenty",
    meta: { label: "Dokumenty", icon: "FileDoneOutlined" },
  },
  {
    name: "edpr-parent",
    meta: { label: "EDPR", icon: "AuditOutlined" },
  },
  {
    name: "edpr_list",
    list: "/edpr",
    meta: { label: "Dokumenty", icon: "FileDoneOutlined", parent: "edpr-parent" },
  },
  {
    name: "edpr_requests",
    list: "/edpr-requests",
    meta: { label: "Wnioski", icon: "FileTextOutlined", parent: "edpr-parent" },
  },
  {
    name: "edpr_queue",
    list: "/edpr/queue",
    meta: { label: "Kolejka", icon: "InboxOutlined", parent: "edpr-parent", tag: "new" },
  },
  {
    name: "bank-parent",
    meta: { label: "Bank", icon: "BankOutlined" },
  },
  {
    name: "bank-pairing",
    list: "/bank/pairing",
    meta: { label: "Parowanie FA ↔ bank", icon: "AuditOutlined", parent: "bank-parent", tag: "new" },
  },
  {
    name: "bank-operations",
    list: "/bank/operations",
    meta: { label: "Operacje", icon: "InboxOutlined", parent: "bank-parent" },
  },
  {
    name: "bank-inbox",
    list: "/bank/inbox",
    meta: { label: "Wysyłka", icon: "InboxOutlined", parent: "bank-parent" },
  },
  {
    name: "bank-payments",
    list: "/bank/payments",
    meta: { label: "Płatności", icon: "DollarOutlined", parent: "bank-parent" },
  },
  {
    name: "payable-invoices",
    list: "/invoices/payable",
    meta: { label: "Do zapłaty", icon: "DollarOutlined", parent: "invoices-parent" },
  },
  {
    name: "bank-account-verification",
    list: "/bank/account-verification",
    meta: {
      label: "Weryfikacja rachunków",
      icon: "SafetyCertificateOutlined",
      parent: "bank-parent",
    },
  },
  {
    name: "bank-statements",
    list: "/bank/statements",
    meta: { label: "Wyciągi", icon: "FileDoneOutlined", parent: "bank-parent" },
  },
  {
    name: "commission",
    meta: { label: "Prowizje", icon: "PercentageOutlined" },
  },
  {
    name: "commission-reports",
    list: "/commission/reports",
    meta: { label: "Mój raport", icon: "BarChartOutlined", parent: "commission" },
  },
  {
    name: "commission-all-reports",
    list: "/commission/reports-list",
    meta: { label: "Wszystkie raporty", icon: "AuditOutlined", parent: "commission" },
  },
  {
    name: "commission-payees",
    list: "/commission/payees",
    meta: { label: "Odbiorcy", icon: "TeamOutlined", parent: "commission" },
  },
  {
    name: "commission-rules",
    list: "/commission/rules",
    meta: { label: "Reguły", icon: "OrderedListOutlined", parent: "commission" },
  },
  {
    name: "commission-edpr-rates",
    list: "/commission/edpr-rates",
    meta: { label: "Stawki EDPR", icon: "DollarOutlined", parent: "commission" },
  },
  {
    name: "commission-config",
    list: "/commission/config",
    meta: { label: "Konfiguracja", icon: "SettingOutlined", parent: "commission" },
  },
  // Hidden resources — still routable, just not in the menu.
  // `edpr_list` / `edpr_requests` moved up under `edpr-parent`
  // so they show in the sider; `edpr_documents` stays hidden
  // (used for navigation routing only — no list page yet).
  {
    name: "edpr_documents",
    meta: { hide: true },
  },
  {
    name: "automatyzacja",
    meta: { label: "Automatyzacja", icon: "RobotOutlined" },
  },
  {
    name: "async_jobs",
    list: "/automatyzacja/async-jobs",
    meta: {
      label: "Zadania asynchroniczne",
      icon: "ThunderboltOutlined",
      parent: "automatyzacja",
    },
  },
  {
    name: "settings",
    meta: { label: "Ustawienia", icon: "SettingOutlined" },
  },
  {
    name: "settings/configuration",
    list: "/settings/configuration",
    meta: { label: "Konfiguracja", icon: "SlidersOutlined", parent: "settings" },
  },
  {
    name: "template_configs",
    list: "/settings/template-configs",
    create: "/settings/template-configs/create",
    edit: "/settings/template-configs/edit/:id",
    meta: { label: "Szablony", icon: "MailOutlined", parent: "settings" },
  },
  {
    name: "settings/drive-setup",
    list: "/settings/drive-setup",
    meta: {
      label: "Google Drive",
      icon: "CloudOutlined",
      parent: "settings",
      hide: true,
    },
  },
  {
    name: "settings/gmail-oauth",
    list: "/settings/gmail-oauth",
    meta: { label: "Gmail Pipeline", icon: "MailOutlined", parent: "settings" },
  },
  {
    name: "transaction_rules",
    list: "/settings/rules",
    create: "/settings/rules/create",
    edit: "/settings/rules/edit/:id",
    meta: { label: "Reguły", icon: "FilterOutlined", parent: "settings" },
  },
  {
    name: "invoice_approval_rules",
    list: "/settings/approval-rules",
    meta: { label: "Auto-zatwierdzanie", icon: "FilterOutlined", parent: "settings" },
  },
  {
    name: "invoice_warning_clauses",
    list: "/settings/warning-clauses",
    meta: { label: "Klauzule ostrzegawcze", icon: "WarningOutlined", parent: "settings" },
  },
  {
    name: "employees",
    list: "/settings/employees",
    meta: { label: "Pracownicy", icon: "TeamOutlined", parent: "settings" },
  },
  {
    name: "reports",
    list: "/settings/reports",
    show: "/settings/reports/:id",
    meta: { label: "Raporty", icon: "BarChartOutlined", parent: "settings" },
  },
  {
    name: "delivery_targets",
    list: "/settings/delivery-targets",
    meta: { label: "Cele dostawcze", icon: "AimOutlined", parent: "settings" },
  },
  {
    name: "settings/audit-log",
    list: "/settings/audit-log",
    meta: { label: "Dziennik zmian", icon: "AuditOutlined", parent: "settings" },
  },
];
